import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft,
  Accessibility,
  CheckCircle2,
  Mail,
  Phone
} from 'lucide-react';

export const AccessibilityStatement = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-[#000080]" data-testid="accessibility-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#000080] to-[#1E3A8A]"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <Accessibility className="w-7 h-7 text-[#34D399]" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Accessibility Statement
            </h1>
          </div>
          <p className="text-lg text-white/80">
            Last Updated: February 27, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="accessibility-content">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto prose prose-lg">
            
            {/* Our Commitment */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Our Commitment to Accessibility</h2>
              <p className="text-[#334155] leading-relaxed">
                We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
              </p>
            </div>

            {/* Accessibility Standards */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Accessibility Standards</h2>
              <p className="text-[#334155] leading-relaxed">
                We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
              </p>
            </div>

            {/* Accessibility Technology & Tools */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Accessibility Technology & Tools</h2>
              <p className="text-[#334155] leading-relaxed mb-4">
                To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
              </p>
              <ul className="space-y-3">
                {[
                  'Screen reader enhancements',
                  'Keyboard navigation improvements',
                  'Color contrast adjustments',
                  'Text size and spacing controls',
                  'Focus indicators',
                  'Additional accessibility interface options'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00A878] flex-shrink-0 mt-0.5" />
                    <span className="text-[#334155]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[#334155] leading-relaxed mt-4">
                This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
              </p>
            </div>

            {/* Ongoing Improvement */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Ongoing Improvement</h2>
              <p className="text-[#334155] leading-relaxed mb-4">
                Accessibility is an ongoing initiative. We are:
              </p>
              <ul className="space-y-3">
                {[
                  'Reviewing site content and functionality regularly',
                  'Working with developers and accessibility tools',
                  'Identifying and remediating barriers',
                  'Incorporating accessibility into future design and development'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00A878] flex-shrink-0 mt-0.5" />
                    <span className="text-[#334155]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[#334155] leading-relaxed mt-4">
                We understand accessibility is a continuous journey and are committed to making improvements over time.
              </p>
            </div>

            {/* Third-Party Content */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Third-Party Content</h2>
              <p className="text-[#334155] leading-relaxed">
                Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
              </p>
            </div>

            {/* Feedback & Assistance */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Feedback & Assistance</h2>
              <p className="text-[#334155] leading-relaxed mb-4">
                If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us. Please include:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'The page URL',
                  'Description of the issue',
                  'Your preferred contact method'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00A878] flex-shrink-0 mt-0.5" />
                    <span className="text-[#334155]">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Contact Card */}
              <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-[#0F172A] mb-4">Contact Us</h3>
                <div className="space-y-3">
                  <a 
                    href="mailto:info@seniorcaresolutionsfl.com" 
                    className="flex items-center gap-3 text-[#000080] hover:text-[#00A878] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    info@seniorcaresolutionsfl.com
                  </a>
                  <a 
                    href="tel:+18773106120" 
                    className="flex items-center gap-3 text-[#000080] hover:text-[#00A878] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    877.310.6120
                  </a>
                </div>
                <Link to="/contact" className="inline-block mt-4">
                  <Button className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full">
                    Visit Contact Page
                  </Button>
                </Link>
              </div>
              
              <p className="text-[#334155] leading-relaxed mt-4">
                We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
              </p>
            </div>

            {/* Good Faith Efforts */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Good Faith Efforts</h2>
              <p className="text-[#334155] leading-relaxed">
                We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
              </p>
            </div>

            {/* Terms & Conditions */}
            <div className="mb-8 bg-[#ECFDF5] rounded-xl p-6 border border-[#00A878]/20">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Terms & Conditions — Accessibility Clause</h2>
              <p className="text-[#334155] leading-relaxed text-sm">
                We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityStatement;
